import { FunctionComponent, JSX, useEffect, useState } from 'react'
import styles from './taskAndNotesStaff.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { ArrowForward, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useLazyQuery } from '@apollo/client'
import { GET_TASK_NOTES_FOR_PROFILE } from '@/modules/API/taskNote/getTaskNotes.ts'
import { TaskNote, TaskNoteProfile } from '@/types/TaskNote.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import AddTasksNotesDialog from '../../../../taskNote/Dialog/addTasksNotesDialog.tsx'
import TaskNoteComponent from '../../../../taskNote/taskNoteComponent/taskNoteComponent.tsx'
import { Daycare, DaycareType } from '@/types/Daycare.ts'
import { StaffWithLinks } from '@/types/Staff.ts'

interface TaskAndNotesStaffProps {
    daycare: Daycare
    staffId: number
    staffName: string
    canUpdate: boolean
    staffsListData: StaffWithLinks[]
}

const TaskAndNotesStaff: FunctionComponent<TaskAndNotesStaffProps> = ({
    daycare,
    staffId,
    staffName,
    canUpdate,
    staffsListData
}: TaskAndNotesStaffProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [tasksAndNotesStaffs, setTasksAndNotesStaffs] = useState<TaskNoteProfile>()
    const [otherTasksAndNotesCount, setOtherTasksAndNotesCount] = useState<number>(0)

    const [showMore, setShowMore] = useState<boolean>(true)

    const [addTasksNotesDialogOpen, setAddTasksNotesDialogOpen] = useState<boolean>(false)

    const [getTaskNotesForProfile, { loading: loadingTaskNotes, data: dataTaskNotes, refetch: refetchTaskNotes }] =
        useLazyQuery(GET_TASK_NOTES_FOR_PROFILE, {
            variables: {
                daycareId: daycare.id,
                staffId
            }
        })

    async function refetchStaffTaskNotes(): Promise<void> {
        const { data } = await refetchTaskNotes()
        if (data) {
            setTasksAndNotesStaffs(data.getTaskNotesForProfile)
            if (data.getTaskNotesForProfile.count > 2) {
                setOtherTasksAndNotesCount(data.getTaskNotesForProfile.count - 2)
            }
        }
    }

    useEffect(() => {
        if (dataTaskNotes && dataTaskNotes.getTaskNotesForProfile) {
            setTasksAndNotesStaffs(dataTaskNotes.getTaskNotesForProfile)
            if (dataTaskNotes.getTaskNotesForProfile.count > 2) {
                setOtherTasksAndNotesCount(dataTaskNotes.getTaskNotesForProfile.count - 2)
            }
        }
    }, [dataTaskNotes])

    useEffect(() => {
        getTaskNotesForProfile()
    }, [daycare])

    const isRPEOrLAEP: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    return (
        <div className={styles.Form}>
            <div className={styles.TitleAndButton}>
                <div className={styles.Title} onClick={() => setShowMore(!showMore)}>
                    <Title text={t('tasksAndNotes')}></Title>
                    {showMore ? (
                        <KeyboardArrowUp className={styles.Icon} />
                    ) : (
                        <KeyboardArrowDown className={styles.Icon} />
                    )}
                </div>
                {canUpdate && showMore && (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddTasksNotesDialogOpen(true)}
                        />
                    </div>
                )}
            </div>
            {loadingTaskNotes && <Loading overall={false} />}
            {showMore && (
                <>
                    {tasksAndNotesStaffs && tasksAndNotesStaffs.count > 0 ? (
                        <div className={styles.TaskNoteContainer}>
                            {tasksAndNotesStaffs.taskNotes.map(
                                (taskNote: TaskNote): JSX.Element => (
                                    <div
                                        className={
                                            tasksAndNotesStaffs.taskNotes.length >= 2
                                                ? styles.TaskNote
                                                : styles.TaskNoteFullWidth
                                        }
                                        key={taskNote.id}
                                    >
                                        <TaskNoteComponent
                                            taskNote={taskNote}
                                            isParent={false}
                                            isRpeOrLaep={isRPEOrLAEP}
                                            canUpdate={canUpdate}
                                            daycareId={daycare.id}
                                            staffs={staffsListData}
                                            handleRefetch={refetchStaffTaskNotes}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <div className={styles.NoTasksNotes}>{t('noTasksAndNotes')}</div>
                    )}
                    {tasksAndNotesStaffs && otherTasksAndNotesCount > 0 && (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                className={styles.Button}
                                customText={
                                    otherTasksAndNotesCount +
                                    ' ' +
                                    `${otherTasksAndNotesCount === 1 ? t('other').toLowerCase() : t('others').toLowerCase()}` +
                                    ' ' +
                                    `${t('tasksAndNotes').toLowerCase()}`
                                }
                                customIcon={<ArrowForward />}
                                onClick={() => navigate('/taskNote?staffId=' + staffId)}
                            />
                        </div>
                    )}
                </>
            )}
            {addTasksNotesDialogOpen && canUpdate && (
                <AddTasksNotesDialog
                    open={addTasksNotesDialogOpen}
                    handleClose={() => setAddTasksNotesDialogOpen(false)}
                    daycareId={daycare.id}
                    staffId={staffId}
                    staffName={staffName}
                    isRpeOrLaep={isRPEOrLAEP}
                    handleRefetch={refetchStaffTaskNotes}
                />
            )}
        </div>
    )
}

export default TaskAndNotesStaff
